import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useAuth, TeamSummary } from '@/contexts/AuthContext';
import {
  teamApi,
  TeamMember,
  TeamUsage,
  TeamDetail,
} from '@/api/teamApi';
import { workspaceApi } from '@/api/workspaceApi';
import { validateAndBuildOverlay, TeamOverlayFields } from '@/lib/workspaceTeamOverlay';
import { PLANS, PlanMeta, EnterpriseContract, EnterpriseContractRequest, EnterprisePriceResponse, enterpriseApi, planApi, CardInfo } from '@/api/planApi';
import { isCardPaymentEnabled, isWorkspaceApiEnabled } from '@/lib/featureFlags';

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import {
  Loader2,
  Plus,
  Users,
  Mail,
  Trash2,
  Crown,
  User as UserIcon,
  AlertCircle,
  CheckCircle2,
  ArrowRightLeft,
  Edit2,
  HelpCircle,
  X,
  BarChart2,
  TrendingUp,
  TrendingDown,
  Building2,
  CreditCard,
} from 'lucide-react';

/** workspaceApi 응답이 4초 안에 안 오면 타임아웃으로 간주하고 abort - legacy 그대로 폴백 */
const WORKSPACE_OVERLAY_TIMEOUT_MS = 4000;

const roleLabel = (role: string) => {
  if (role === 'OWNER') return '팀장';
  return '멤버';
};

const roleIcon = (role: string) => {
  if (role === 'OWNER') return <Crown className="w-3 h-3 text-yellow-500" />;
  return <UserIcon className="w-3 h-3 text-gray-400" />;
};

const canManageTeam = (myRole: string, userRole: string) =>
  myRole === 'OWNER' || userRole === 'SUPER_ADMIN';

const canInvite = (myRole: string, userRole: string) =>
  myRole === 'OWNER' || userRole === 'SUPER_ADMIN';

const canRemoveMember = (myRole: string, targetRole: string, userRole: string) => {
  if (userRole === 'SUPER_ADMIN') return true;
  if (myRole === 'OWNER') return true;
  return false;
};

const canChangeRole = (myRole: string, userRole: string) =>
  myRole === 'OWNER' || userRole === 'SUPER_ADMIN';

const canViewAllUsage = (myRole: string, userRole: string) =>
  myRole === 'OWNER' || userRole === 'SUPER_ADMIN';

export const TeamManagement: React.FC = () => {
  const { user, myTeams, activeTeamId, setActiveTeamId, setMyTeams } = useAuth();
  const userRole = user?.userRole ?? 'CLIENT';

  // Selected team state
  const [selectedTeamId, setSelectedTeamId] = useState<number | null>(
    activeTeamId ?? (myTeams.length > 0 ? myTeams[0].teamId : null)
  );

  // workspaceApi.getMyTeams()로 status/plan/monthlyLimit/memberCount만 덮어쓰는 overlay.
  // myTeams(legacy)가 항상 팀 목록/ID/역할의 유일한 진실 소스이고, 이 overlay는 성공+
  // 검증 통과 시에만 표시 필드 일부를 보강한다 - 실패(에러/타임아웃/부분 응답 이상)
  // 시엔 전부 legacy 그대로 렌더링되므로 이 컴포넌트가 "멈추는" 경로가 없다.
  const [workspaceOverlay, setWorkspaceOverlay] = useState<Record<number, TeamOverlayFields>>({});

  useEffect(() => {
    if (!isWorkspaceApiEnabled) {
      setWorkspaceOverlay({});
      return;
    }
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), WORKSPACE_OVERLAY_TIMEOUT_MS);

    workspaceApi
      .getMyTeams({ signal: controller.signal })
      .then((res) => {
        const overlay = validateAndBuildOverlay(res.data, myTeams);
        setWorkspaceOverlay(overlay);
        if (Object.keys(overlay).length === 0 && myTeams.length > 0) {
          console.warn('[workspace-api] invalid/mismatched response, falling back to legacy display');
        }
      })
      .catch((err) => {
        console.warn('[workspace-api] getMyTeams failed/timed out, falling back to legacy display', err);
        setWorkspaceOverlay({});
      })
      .finally(() => clearTimeout(timeoutId));

    return () => {
      controller.abort();
      clearTimeout(timeoutId);
    };
  }, [myTeams]);

  const displayTeams = useMemo<TeamSummary[]>(
    () => myTeams.map((t) => ({ ...t, ...workspaceOverlay[t.teamId] })),
    [myTeams, workspaceOverlay]
  );

  const selectedTeam = displayTeams.find((t) => t.teamId === selectedTeamId) ?? null;
  const myRole = selectedTeam?.myRole ?? 'MEMBER';

  // myTeams는 DashboardLayout에서 비동기(react-query)로 로드되어 이 컴포넌트의
  // 첫 렌더 이후에 채워지는 경우가 대부분이라, selectedTeamId의 useState 초기값만으로는
  // 팀이 도착한 뒤에도 계속 null로 남을 수 있다. 목록이 채워졌는데 현재 선택이
  // 비어있거나(null) 더 이상 유효하지 않으면(삭제 등) 다시 골라준다.
  useEffect(() => {
    if (myTeams.length === 0) return;
    const stillValid = selectedTeamId != null && myTeams.some((t) => t.teamId === selectedTeamId);
    if (!stillValid) {
      setSelectedTeamId(activeTeamId ?? myTeams[0].teamId);
    }
  }, [myTeams, activeTeamId, selectedTeamId]);
  const ownedTeamsCount = myTeams.filter((t) => t.myRole === 'OWNER').length;
  // 사용자는 하나의 팀에만 소속될 수 있음 (서버는 상태와 무관하게 기존 팀 멤버십이 있으면 생성을 막음 - TeamService.createTeam 참고)
  const canCreateTeam = userRole === 'SUPER_ADMIN' || myTeams.length === 0;

  // Data states
  const [members, setMembers] = useState<TeamMember[]>([]);
  const membersRef = React.useRef<TeamMember[]>([]);
  const [usage, setUsage] = useState<TeamUsage | null>(null);
  const [teamDetail, setTeamDetail] = useState<TeamDetail | null>(null);

  const [loadingMembers, setLoadingMembers] = useState(false);
  const [loadingUsage, setLoadingUsage] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Create team dialog
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newTeamName, setNewTeamName] = useState('');

  // 결제수단 선택 모달 (팀 생성 / 엔터프라이즈 계약 신규 생성에서 공용으로 사용)
  const [cards, setCards] = useState<CardInfo[]>([]);
  const [paymentMethodDialog, setPaymentMethodDialog] = useState<{
    open: boolean;
    title: string;
    priceLabel: string;
    onSelect: (method: 'CARD' | 'BANK_TRANSFER') => void;
  }>({ open: false, title: '', priceLabel: '', onSelect: () => {} });
  const [paymentMethodSubmitting, setPaymentMethodSubmitting] = useState(false);

  // Edit team name
  const [editingTeamName, setEditingTeamName] = useState(false);
  const [editTeamNameValue, setEditTeamNameValue] = useState('');

  // Invite form
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteResult, setInviteResult] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  // Transfer owner dialog
  const [showTransferDialog, setShowTransferDialog] = useState(false);
  const [transferTargetUserId, setTransferTargetUserId] = useState('');

  // Delete team confirm
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Leave team confirm
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);

  // Change team plan dialog
  const [showPlanDialog, setShowPlanDialog] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<'STARTER' | 'PROFESSIONAL' | 'ENTERPRISE'>('STARTER');

  // 엔터프라이즈 모달 상태
  const [enterpriseModalOpen, setEnterpriseModalOpen] = useState(false);
  const [enterpriseUsage, setEnterpriseUsage] = useState<number>(6000);
  const [enterprisePrice, setEnterprisePrice] = useState<number>(0);
  const [enterprisePriceInfo, setEnterprisePriceInfo] = useState<EnterprisePriceResponse | null>(null);
  const [enterprisePricingTiers, setEnterprisePricingTiers] = useState<Array<{ tier: string; unitPrice: number }>>([]);
  const [enterpriseContract, setEnterpriseContract] = useState<EnterpriseContract | null>(null);
  const [enterpriseSaving, setEnterpriseSaving] = useState(false);
  const [enterpriseError, setEnterpriseError] = useState<string | null>(null);
  const [enterpriseAutoRenewal, setEnterpriseAutoRenewal] = useState(false);
  const [downgradeResult, setDowngradeResult] = useState<{ open: boolean; message: string; effectiveDate?: string | null; nextBillingAmount?: number | null }>({
    open: false,
    message: '',
  });

  const clearMessages = () => {
    setError(null);
    setSuccess(null);
  };

  // Load members for selected team
  const loadMembers = useCallback(async (teamId: number): Promise<TeamMember[]> => {
    setLoadingMembers(true);
    try {
      const res = await teamApi.getMembers(teamId);
      membersRef.current = res.data;
      setMembers(res.data);
      return res.data;
    } catch {
      membersRef.current = [];
      setMembers([]);
      return [];
    } finally {
      setLoadingMembers(false);
    }
  }, []);

  // Load usage for selected team (API에서 멤버별 사용량 포함)
  const loadUsage = useCallback(async (teamId: number) => {
    setLoadingUsage(true);
    try {
      const res = await teamApi.getUsage(teamId);
      setUsage(res.data);
    } catch {
      setUsage(null);
    } finally {
      setLoadingUsage(false);
    }
  }, []);

  // Load enterprise contract for selected team
  const loadEnterpriseContract = useCallback(async (teamId: number) => {
    try {
      const res = await enterpriseApi.getTeamContract(teamId);
      const contract = res.data ?? null;
      setEnterpriseContract(contract);
      // 계약이 있는 경우 autoRenewal 값 설정
      if (contract) {
        setEnterpriseAutoRenewal(contract.autoRenewal ?? false);
      } else {
        setEnterpriseAutoRenewal(false);
      }
    } catch {
      setEnterpriseContract(null);
      setEnterpriseAutoRenewal(false);
    }
  }, []);

  // 등록된 카드 목록 로드 (결제수단 선택 모달에서 안내용으로 사용)
  const loadCards = useCallback(async () => {
    try {
      const res = await planApi.getCards();
      setCards(Array.isArray(res.data) ? res.data : []);
    } catch {
      setCards([]);
    }
  }, []);

  useEffect(() => {
    loadCards();
  }, [loadCards]);

  // Reload my teams list
  const reloadMyTeams = useCallback(async () => {
    try {
      const res = await teamApi.getMyTeams();
      // axios 인터셉터 변환 반영: res.data가 이미 변환된 형태
      const data = Array.isArray(res.data)
        ? res.data
        : (res.data as any)?.content ?? [];
      setMyTeams(data);
    } catch {
      /* ignore */
    }
  }, [setMyTeams]);

  useEffect(() => {
    if (!selectedTeamId) {
      setMembers([]);
      setUsage(null);
      setEnterpriseContract(null);
      return;
    }
    const init = async () => {
      await loadMembers(selectedTeamId);
      await loadUsage(selectedTeamId);
      await loadEnterpriseContract(selectedTeamId);
    };
    init();
  }, [selectedTeamId, loadMembers, loadUsage, loadEnterpriseContract]);

  useEffect(() => {
    if (!enterpriseModalOpen) return;
    // 엔터프라이즈 가격표 동적 로드 (실제 백엔드 가격 정책과 항상 일치하도록,
    // 하드코딩된 정적 가격표 대신 대표 사용량에 대해 실제 API를 호출해 구성)
    const tierUsages = [5001, 6000, 11001, 21001];
    Promise.all(
      tierUsages.map(usage =>
        enterpriseApi.calculatePrice(usage)
          .then(res => ({ tier: res.data.tier, unitPrice: res.data.unitPrice }))
          .catch(() => null)
      )
    ).then(results => {
      setEnterprisePricingTiers(results.filter(Boolean) as Array<{ tier: string; unitPrice: number }>);
    });
  }, [enterpriseModalOpen]);

  // — Create team —
  const submitCreateTeam = async (name: string, paymentMethod?: 'CARD' | 'BANK_TRANSFER') => {
    setActionLoading(true);
    try {
      const res = await teamApi.createTeam(name, paymentMethod);
      await reloadMyTeams();
      setSelectedTeamId(res.data.teamId);
      setActiveTeamId(res.data.teamId);
      setNewTeamName('');
      setShowCreateDialog(false);
      closePaymentMethodDialog();
      setSuccess(
        res.data.status === 'SUSPENDED'
          ? '팀이 생성되었습니다. 계좌이체 입금 확인 후 이용하실 수 있습니다.'
          : '팀이 생성되었습니다.'
      );
    } catch (err: any) {
      setError(err.response?.data?.message || '팀 생성에 실패했습니다.');
    } finally {
      setActionLoading(false);
      setPaymentMethodSubmitting(false);
    }
  };

  const handlePaymentMethodSelect = (method: 'CARD' | 'BANK_TRANSFER') => {
    setPaymentMethodSubmitting(true);
    paymentMethodDialog.onSelect(method);
  };

  const closePaymentMethodDialog = () =>
    setPaymentMethodDialog({ open: false, title: '', priceLabel: '', onSelect: () => {} });

  const handleCreateTeam = async (e: React.FormEvent) => {
    e.preventDefault();
    clearMessages();
    const name = newTeamName.trim();
    if (!name) return;

    // 신규 결제가 필요한 경우(팀장이 FREE)에만 결제수단을 명시적으로 물어봄 -
    // 팀장이 이미 유료면 결제 없이 플랜을 상속하므로 기존처럼 바로 생성
    if (user?.subscriptionPlan === 'FREE') {
      const starterPrice = PLANS.find(p => p.code === 'STARTER')?.price ?? 9900;
      setShowCreateDialog(false);
      setPaymentMethodDialog({
        open: true,
        title: '팀 생성 결제 방법 선택',
        priceLabel: `스타트 플랜 ₩${starterPrice.toLocaleString()}/월 (부가세 포함)`,
        onSelect: (method) => submitCreateTeam(name, method),
      });
    } else {
      await submitCreateTeam(name);
    }
  };

  // — Update team name —
  const handleUpdateTeamName = async (e: React.FormEvent) => {
    e.preventDefault();
    clearMessages();
    if (!selectedTeamId || !editTeamNameValue.trim()) return;
    setActionLoading(true);
    try {
      await teamApi.updateTeam(selectedTeamId, editTeamNameValue.trim());
      await reloadMyTeams();
      setEditingTeamName(false);
      setSuccess('팀 이름이 변경되었습니다.');
    } catch (err: any) {
      setError(err.response?.data?.message || '팀 이름 변경에 실패했습니다.');
    } finally {
      setActionLoading(false);
    }
  };

  // — Delete team —
  // 확인은 showDeleteConfirm 커스텀 다이얼로그가 이미 담당하므로 여기서 window.confirm을 또 띄우지 않는다.
  const handleDeleteTeam = async () => {
    if (!selectedTeamId) return;
    clearMessages();
    setActionLoading(true);
    try {
      await teamApi.deleteTeam(selectedTeamId);
      await reloadMyTeams();
      setSelectedTeamId(null);
      setActiveTeamId(null);
      setShowDeleteConfirm(false);
      setSuccess('팀이 삭제되었습니다.');
    } catch (err: any) {
      setError(err.response?.data?.message || '팀 삭제에 실패했습니다.');
      setShowDeleteConfirm(false);
    } finally {
      setActionLoading(false);
    }
  };

  // — Send invite —
  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    clearMessages();
    setInviteResult(null);
    if (!selectedTeamId || !inviteEmail.trim()) return;
    setInviteLoading(true);
    try {
      const email = inviteEmail.trim();
      await teamApi.inviteMember(selectedTeamId, email);
      setInviteEmail('');
      setInviteResult({ type: 'success', message: `초대 메일이 발송되었습니다.\n${email} 주소로 초대 링크가 전송됩니다.` });
    } catch (err: any) {
      const status = err.response?.status;
      const data = err.response?.data;
      let msg: string;
      if (status === 403) {
        msg = '초대 권한이 없습니다.';
      } else if (status === 404) {
        msg = '팀을 찾을 수 없습니다.';
      } else if (status === 409) {
        msg = data?.message ?? '이미 초대 중이거나 팀 멤버입니다.';
      } else {
        msg = data?.message ?? data?.errors?.[0]?.message ?? '초대 발송에 실패했습니다.';
      }
      setInviteResult({ type: 'error', message: msg });
    } finally {
      setInviteLoading(false);
    }
  };

  // — Change member role —
  const handleChangeRole = async (userId: string, newRole: 'MEMBER') => {
    if (!selectedTeamId) return;
    clearMessages();
    setActionLoading(true);
    try {
      await teamApi.changeMemberRole(selectedTeamId, userId, newRole);
      await loadMembers(selectedTeamId);
      await loadUsage(selectedTeamId);
      setSuccess('역할이 변경되었습니다.');
    } catch (err: any) {
      setError(err.response?.data?.message || '역할 변경에 실패했습니다.');
    } finally {
      setActionLoading(false);
    }
  };

  // — Remove member —
  const handleRemoveMember = async (userId: string) => {
    if (!selectedTeamId) return;
    if (!window.confirm(
      '팀에서 제거하시겠습니까?\n' +
      '제거된 멤버는 즉시 팀 접근 권한을 잃으며, 개인 요금제가 곧바로 무료(FREE) 플랜으로 전환됩니다.'
    )) return;
    clearMessages();
    setActionLoading(true);
    try {
      await teamApi.removeMember(selectedTeamId, userId);
      await loadMembers(selectedTeamId);
      setSuccess('멤버가 제거되었습니다.');
    } catch (err: any) {
      setError(err.response?.data?.message || '멤버 제거에 실패했습니다.');
    } finally {
      setActionLoading(false);
    }
  };

  // — Leave team —
  const handleLeaveTeam = async () => {
    if (!selectedTeamId) return;
    if (!window.confirm('정말로 팀을 떠나시겠습니까?')) return;
    clearMessages();
    setActionLoading(true);
    try {
      const res = await teamApi.leaveTeam(selectedTeamId);
      const pendingDate = new Date(res.data.pendingRemovalDate);
      const formattedDate = pendingDate.toLocaleDateString('ko-KR');

      setSuccess(
        `팀 나가기가 예약되었습니다.\n` +
        `${formattedDate}에 팀에서 완전히 나가게 됩니다.\n` +
        `이후 개인 요금제(${res.data.restoredPlan})로 복구됩니다.`
      );

      await reloadMyTeams();
      setSelectedTeamId(null);
      setActiveTeamId(null);
    } catch (err: any) {
      setError(err.response?.data?.message || '팀 나가기에 실패했습니다.');
    } finally {
      setActionLoading(false);
    }
  };

  // — Transfer owner —
  const handleTransferOwner = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedTeamId || !transferTargetUserId.trim()) return;
    clearMessages();
    setActionLoading(true);
    try {
      await teamApi.transferOwner(selectedTeamId, transferTargetUserId.trim());
      await reloadMyTeams();
      await loadMembers(selectedTeamId);
      await loadUsage(selectedTeamId);
      setShowTransferDialog(false);
      setTransferTargetUserId('');
      setSuccess('팀장 권한이 이전되었습니다.');
    } catch (err: any) {
      setError(err.response?.data?.message || '팀장 이전에 실패했습니다.');
    } finally {
      setActionLoading(false);
    }
  };

  // — Change team plan —
  const handleChangeTeamPlan = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedTeamId) return;
    clearMessages();
    setActionLoading(true);
    try {
      const res = await teamApi.changeTeamPlan(selectedTeamId, selectedPlan);
      if (res.data.success) {
        await reloadMyTeams();
        await loadUsage(selectedTeamId);
        await loadEnterpriseContract(selectedTeamId);
        setShowPlanDialog(false);
        setSuccess('팀 요금제가 변경되었습니다.');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || '팀 요금제 변경에 실패했습니다.');
    } finally {
      setActionLoading(false);
    }
  };

  // 엔터프라이즈 모달 핸들러
  const handleEnterpriseUsageChange = (value: string) => {
    const usage = parseInt(value);
    if (usage >= 5001) {
      setEnterpriseUsage(usage);
      setEnterpriseError(null);
      // 가격 계산
      enterpriseApi.calculatePrice(usage).then(res => {
        setEnterprisePrice(res.data.price);
        setEnterprisePriceInfo(res.data);
      }).catch(() => {
        // 클라이언트 사이드 계산 (fallback) - EnterpriseService.calculatePrice와 동일한 누진 티어
        let price = 29000; // 1구간(1~5,000건) 고정
        if (usage > 5000 && usage <= 10000) price += (usage - 5000) * 4;
        else if (usage > 10000 && usage <= 20000) price += 5000 * 4 + (usage - 10000) * 3;
        else if (usage > 20000) price += 5000 * 4 + 10000 * 3 + (usage - 20000) * 2;
        setEnterprisePrice(price);
      });
    }
  };

  const submitEnterpriseSave = async (paymentMethod?: 'CARD' | 'BANK_TRANSFER') => {
    if (!selectedTeamId) return;

    setEnterpriseSaving(true);
    setEnterpriseError(null);

    try {
      const today = new Date();
      const startDate = today.toISOString().split('T')[0];
      const endDate = new Date(today.getFullYear() + 1, today.getMonth(), today.getDate())
        .toISOString().split('T')[0];

      const contractData: EnterpriseContractRequest = {
        contractName: `${selectedTeam?.teamName || '팀'} 엔터프라이즈 계약`,
        monthlyLimit: enterpriseUsage,
        monthlyPrice: enterprisePrice,
        startDate,
        endDate,
        usage: enterpriseUsage,
        autoRenewal: enterpriseAutoRenewal,
        paymentMethod,
      };

      if (!enterpriseContract) {
        // 신규 계약 생성
        const res = await enterpriseApi.createTeamContract(selectedTeamId, contractData);
        const createdStatus = res.data?.data?.status;
        setSuccess(
          createdStatus === 'PENDING'
            ? '엔터프라이즈 계약이 생성되었습니다. 계좌이체 입금 확인 후 이용하실 수 있습니다.'
            : '엔터프라이즈 계약이 생성되어 즉시 적용되었습니다.'
        );
        closePaymentMethodDialog();
      } else {
        // 기존 계약 수정
        const response = await enterpriseApi.updateTeamContract(selectedTeamId, enterpriseContract.id, contractData);
        const responseData = response.data?.data;
        // 다운그레이드 응답 처리
        if (responseData?.isDowngrade) {
          setDowngradeResult({
            open: true,
            message: responseData.downgradeMessage || '다운그레이드가 예약되었습니다.',
            effectiveDate: responseData.downgradeEffectiveDate,
            nextBillingAmount: responseData.nextBillingAmount,
          });
        } else {
          setSuccess('엔터프라이즈 계약이 수정되었습니다.');
        }
      }

      setEnterpriseModalOpen(false);
      await reloadMyTeams();
      await loadEnterpriseContract(selectedTeamId);
    } catch (err: any) {
      setEnterpriseError(err?.response?.data?.message ?? '엔터프라이즈 계약 저장 중 오류가 발생했습니다.');
    } finally {
      setEnterpriseSaving(false);
      setPaymentMethodSubmitting(false);
    }
  };

  const handleEnterpriseSave = async () => {
    if (!selectedTeamId) return;

    // PENDING 상태 체크 - 입금 대기 중인 경우 차단
    if (enterpriseContract?.status === 'PENDING') {
      setEnterpriseError('엔터프라이즈 계약 입금 대기 중입니다. 입금 확인 후 추가 변경을 요청해 주세요.');
      return;
    }

    // 현재 계약과 동일한지 체크
    if (enterpriseContract && enterpriseContract.monthlyLimit === enterpriseUsage) {
      setEnterpriseError('현재와 동일한 사용량입니다. 변경할 내용이 없으면 저장되지 않습니다.');
      return;
    }

    if (!enterpriseContract) {
      // 신규 계약: 결제수단을 명시적으로 선택하게 함 (기존 계약 수정은 이번 스코프 제외 - 기존 정책 그대로 유지)
      setEnterpriseModalOpen(false);
      setPaymentMethodDialog({
        open: true,
        title: '엔터프라이즈 계약 결제 방법 선택',
        priceLabel: `엔터프라이즈 계약 ₩${enterprisePrice.toLocaleString()}/월 (부가세 포함)`,
        onSelect: (method) => submitEnterpriseSave(method),
      });
    } else {
      await submitEnterpriseSave();
    }
  };

  // ─── No teams: show CTA ─── (SUSPENDED 등 상태와 무관하게 myTeams에 있으면 "팀 있음"으로 취급)
  if (myTeams.length === 0) {
    return (
      <div className="space-y-4">
        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        {success && (
          <Alert className="bg-green-50 border-green-200">
            <CheckCircle2 className="h-4 w-4 text-green-600" />
            <AlertDescription className="text-green-800">{success}</AlertDescription>
          </Alert>
        )}

        <Card className="text-center py-10">
          <CardContent>
            <Users className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 mb-6">소속된 팀이 없습니다.</p>
            <Button onClick={() => setShowCreateDialog(true)} disabled={!canCreateTeam}>
              <Plus className="w-4 h-4 mr-2" />
              팀 만들기
            </Button>
            {!canCreateTeam && (
              <p className="text-xs text-gray-400 mt-2">팀은 최대 2개까지 생성할 수 있습니다.</p>
            )}
          </CardContent>
        </Card>

        {showCreateDialog && (
          <CreateTeamDialog
            value={newTeamName}
            onChange={setNewTeamName}
            onSubmit={handleCreateTeam}
            onClose={() => { setShowCreateDialog(false); setNewTeamName(''); }}
            loading={actionLoading}
          />
        )}
        <PaymentMethodSelectDialog
          open={paymentMethodDialog.open}
          title={paymentMethodDialog.title}
          priceLabel={paymentMethodDialog.priceLabel}
          cards={cards}
          submitting={paymentMethodSubmitting}
          onSelect={handlePaymentMethodSelect}
          onClose={closePaymentMethodDialog}
        />
      </div>
    );
  }

  // ─── Has teams ───
  const usagePercent = usage
    ? Math.min(100, Math.round((usage.totalUsed / Math.max(usage.monthlyLimit, 1)) * 100))
    : 0;

  return (
    <div className="space-y-6">
      {/* 팀 관리 안내 */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <HelpCircle className="w-4 h-4" />
            팀 관리 안내
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 text-sm">
          <div>
            <h3 className="font-semibold text-gray-800 mb-2">팀 역할 안내</h3>
            <div className="space-y-2 text-gray-600 text-xs">
              <div>
                <p className="font-medium text-gray-700">팀장 (OWNER)</p>
                <p className="mt-1">팀을 생성한 사용자입니다. 팀 요금제를 결정하고, 팀원 초대/제거, 팀장 권한 이전 등 팀 관리 전체 권한을 가집니다.</p>
              </div>
              <div>
                <p className="font-medium text-gray-700">팀원 (MEMBER)</p>
                <p className="mt-1">팀에 소속된 일반 사용자입니다. 본인 사용량만 확인할 수 있고, 팀에서 나갈 수 있습니다.</p>
              </div>
            </div>
          </div>
          <div>
            <h3 className="font-semibold text-gray-800 mb-2">팀 소속 제한</h3>
            <p className="text-xs text-gray-600">사용자는 하나의 팀에만 소속될 수 있습니다. 새로운 팀을 만들려면 먼저 현재 팀에서 나가거나 팀을 삭제해야 합니다.</p>
          </div>
          <div>
            <h3 className="font-semibold text-gray-800 mb-2">팀원 초대</h3>
            <p className="text-xs text-gray-600">초대할 팀원은 ALT Generation 회원으로 가입되어야 합니다. 가입 시 해당 이메일로 초대 링크가 발송되며, 링크를 통해 팀에 가입할 수 있습니다 (유효기간 7일).</p>
          </div>
          <div>
            <h3 className="font-semibold text-gray-800 mb-2">팀장 이전</h3>
            <p className="text-xs text-gray-600">팀장 권한을 다른 팀원에게 이전할 수 있습니다. 팀장 변경 시 팀 요금제는 유지되며, 새 팀장의 개인 요금제로 변경되지 않습니다. 팀장이 팀을 떠나기 전에 권한을 이전해야 합니다.</p>
          </div>
          <div>
            <h3 className="font-semibold text-gray-800 mb-2">팀 요금제</h3>
            <p className="text-xs text-gray-600">팀의 요금제는 팀장의 개인 요금제를 따릅니다. 팀장이 요금제를 변경하면 팀 전체에 즉시 반영됩니다. 팀 이번 달 사용 기간도 팀장의 첫 결제일 기준으로 계산됩니다.</p>
          </div>
        </CardContent>
      </Card>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      {success && (
        <Alert className="bg-green-50 border-green-200">
          <CheckCircle2 className="h-4 w-4 text-green-600" />
          <AlertDescription className="text-green-800">{success}</AlertDescription>
        </Alert>
      )}

      {/* Team Selector */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex gap-2 flex-wrap">
          {displayTeams.map((t) => (
            <button
              key={t.teamId}
              onClick={() => setSelectedTeamId(t.teamId)}
              className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${
                selectedTeamId === t.teamId
                  ? 'bg-primary text-white border-primary'
                  : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
              }`}
            >
              {t.teamName}
              <span className="ml-1 text-xs opacity-70">({roleLabel(t.myRole)})</span>
            </button>
          ))}
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowCreateDialog(true)}
          disabled={!canCreateTeam}
          title={!canCreateTeam ? '팀은 최대 2개까지 생성할 수 있습니다.' : undefined}
        >
          <Plus className="w-4 h-4 mr-1" />
          새 팀
        </Button>
      </div>

      {selectedTeam && (
        <>
          {selectedTeam.status === 'SUSPENDED' && (
            <Alert className="border-orange-200 bg-orange-50">
              <AlertCircle className="h-4 w-4 text-orange-600" />
              <AlertDescription className="text-orange-800">
                결제 확인 대기 중이거나 정기결제 실패로 팀 이용이 제한된 상태입니다. 계좌이체 입금 확인 또는 결제 수단 점검 후 이용하실 수 있습니다.
              </AlertDescription>
            </Alert>
          )}
          {/* Team Info Card */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div className="flex items-center gap-3">
                {editingTeamName ? (
                  <form onSubmit={handleUpdateTeamName} className="flex items-center gap-2">
                    <Input
                      value={editTeamNameValue}
                      onChange={(e) => setEditTeamNameValue(e.target.value)}
                      className="h-8 text-sm"
                      autoFocus
                      disabled={actionLoading}
                    />
                    <Button type="submit" size="sm" disabled={actionLoading}>
                      {actionLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : '저장'}
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => setEditingTeamName(false)}
                      disabled={actionLoading}
                    >
                      취소
                    </Button>
                  </form>
                ) : (
                  <div className="flex items-center gap-2">
                    <CardTitle>{selectedTeam.teamName}</CardTitle>
                    {canManageTeam(myRole, userRole) && (
                      <button
                        onClick={() => {
                          setEditTeamNameValue(selectedTeam.teamName);
                          setEditingTeamName(true);
                        }}
                        className="text-gray-400 hover:text-gray-600"
                        title="팀 이름 변경"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Badge className="bg-blue-100 text-blue-700">요금제: {selectedTeam.plan === 'FREE' ? '무료' : selectedTeam.plan === 'STARTER' ? '스타트' : selectedTeam.plan === 'PROFESSIONAL' ? '프로' : selectedTeam.plan === 'ENTERPRISE' ? '엔터프라이즈' : selectedTeam.plan ?? '무료'}</Badge>
                {/* 요금제 변경은 /dashboard/my/plan에서만 가능 */}
                {canManageTeam(myRole, userRole) && (
                  <>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowTransferDialog(true)}
                      title="팀장 권한 이전"
                    >
                      <ArrowRightLeft className="w-4 h-4 mr-1" />
                      팀장 이전
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-red-600 hover:text-red-700 border-red-200 hover:bg-red-50"
                      onClick={() => setShowDeleteConfirm(true)}
                    >
                      <Trash2 className="w-4 h-4 mr-1" />
                      팀 삭제
                    </Button>
                  </>
                )}
                {myRole === 'MEMBER' && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-red-600 hover:text-red-700 border-red-200 hover:bg-red-50"
                    onClick={handleLeaveTeam}
                  >
                    <Trash2 className="w-4 h-4 mr-1" />
                    팀 나가기
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent className="text-sm text-gray-600 flex gap-4">
              <span>멤버 {members.length}명</span>
              <span>월 한도 {selectedTeam.monthlyLimit?.toLocaleString() ?? '-'}건</span>
              <span>
                {selectedTeam.status === 'SUSPENDED' ? '이용 제한 (결제 확인 필요)' : '정상'}
              </span>
            </CardContent>
          </Card>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Members Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  팀원 목록
                </CardTitle>
                <CardDescription>팀원 역할 및 상태 관리</CardDescription>
              </CardHeader>
              <CardContent>
                {loadingMembers ? (
                  <div className="flex justify-center py-6">
                    <Loader2 className="w-6 h-6 animate-spin text-primary" />
                  </div>
                ) : members.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-4">팀원이 없습니다.</p>
                ) : (
                  <div className="space-y-2">
                    {members.map((member) => {
                      const isPendingRemoval = member.status === 'PENDING_REMOVAL';
                      return (
                        <div
                          key={member.userId}
                          className={`flex items-center justify-between p-3 rounded-lg border ${
                            isPendingRemoval ? 'bg-orange-50 border-orange-200' : 'bg-white border-gray-100'
                          }`}
                        >
                          <div className="flex items-center gap-2 min-w-0">
                            {roleIcon(member.role)}
                            <div className="min-w-0">
                              <p className="text-sm font-medium truncate">{member.name}</p>
                              <p className="text-xs text-gray-500 truncate">{member.email}</p>
                            </div>
                            <Badge
                              className={
                                member.role === 'OWNER'
                                  ? 'bg-yellow-100 text-yellow-700 ml-1'
                                  : 'bg-gray-100 text-gray-600 ml-1'
                              }
                            >
                              {roleLabel(member.role)}
                            </Badge>
                            {isPendingRemoval && (
                              <Badge className="bg-orange-100 text-orange-700 ml-1">
                                제거 예정
                              </Badge>
                            )}
                          </div>
                          {/* Action buttons — disabled for PENDING_REMOVAL */}
                          {member.role !== 'OWNER' && !isPendingRemoval && (
                            <div className="flex items-center gap-1 ml-2 shrink-0">
                              {canRemoveMember(myRole, member.role, userRole) && (
                                <button
                                  onClick={() => handleRemoveMember(member.userId)}
                                  disabled={actionLoading}
                                  className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded disabled:opacity-40"
                                  title="팀에서 제거"
                                >
                                  <X className="w-4 h-4" />
                                </button>
                              )}
                            </div>
                          )}
                          {isPendingRemoval && (
                            <span className="ml-2 text-xs text-orange-500 shrink-0">
                              다음달 자동 삭제
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Invite Card */}
            {canInvite(myRole, userRole) && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Mail className="w-4 h-4" />
                    팀원 초대
                  </CardTitle>
                  <CardDescription>초대할 팀원은 ALT Generation 회원으로 가입되어야 합니다. 가입 시 해당 이메일로 초대 링크가 발송됩니다 (유효기간 7일)</CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleInvite} className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium mb-1">이메일</label>
                      <Input
                        type="email"
                        value={inviteEmail}
                        onChange={(e) => setInviteEmail(e.target.value)}
                        placeholder="invite@example.com"
                        required
                        disabled={inviteLoading}
                      />
                    </div>
                    <Button type="submit" disabled={inviteLoading || !inviteEmail.trim()} className="w-full">
                      {inviteLoading ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" />발송 중...</>
                      ) : (
                        <><Mail className="w-4 h-4 mr-2" />초대 메일 발송</>
                      )}
                    </Button>
                    {inviteResult && (
                      <div
                        className={`flex items-start gap-2 rounded-lg px-3 py-2 text-sm ${
                          inviteResult.type === 'success'
                            ? 'bg-green-50 border border-green-200 text-green-800'
                            : 'bg-red-50 border border-red-200 text-red-700'
                        }`}
                      >
                        {inviteResult.type === 'success'
                          ? <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0 text-green-600" />
                          : <AlertCircle className="w-4 h-4 mt-0.5 shrink-0 text-red-500" />}
                        <span className="whitespace-pre-line">{inviteResult.message}</span>
                      </div>
                    )}
                  </form>
                </CardContent>
              </Card>
            )}

            {/* Usage Card */}
            <Card className={canInvite(myRole, userRole) ? 'md:col-span-2' : ''}>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart2 className="w-4 h-4" />
                  팀 사용량
                  {usage && (
                    <span className="text-sm font-normal text-gray-500 ml-1">
                      ({usage.month})
                    </span>
                  )}
                </CardTitle>
                <CardDescription>이번 달 팀 ALT 생성 현황</CardDescription>
              </CardHeader>
              <CardContent>
                {loadingUsage ? (
                  <div className="flex justify-center py-6">
                    <Loader2 className="w-6 h-6 animate-spin text-primary" />
                  </div>
                ) : usage ? (
                  <div className="space-y-4">
                    {/* Progress bar */}
                    <div>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-gray-600">
                          사용: <span className="font-semibold text-gray-900">{usage.totalUsed.toLocaleString()}</span>건
                        </span>
                        <span className="text-gray-600">
                          한도: <span className="font-semibold">{usage.monthlyLimit.toLocaleString()}</span>건
                        </span>
                      </div>
                      <div className="w-full bg-gray-100 rounded-full h-2.5">
                        <div
                          className={`h-2.5 rounded-full transition-all ${
                            usagePercent >= 90
                              ? 'bg-red-500'
                              : usagePercent >= 70
                              ? 'bg-orange-400'
                              : 'bg-primary'
                          }`}
                          style={{ width: `${usagePercent}%` }}
                        />
                      </div>
                      <p className="text-xs text-gray-500 mt-1 text-right">
                        잔여 {usage.remaining.toLocaleString()}건 ({100 - usagePercent}%)
                      </p>
                    </div>

                    {/* Billing period */}
                    {usage.billingStartDate && usage.billingEndDate && (
                      <div className="text-sm text-gray-500">
                        팀 이번 달 사용 기간: {new Date(usage.billingStartDate).toLocaleDateString('ko-KR')} ~ {new Date(usage.billingEndDate).toLocaleDateString('ko-KR')} (팀장 기준)
                      </div>
                    )}

                    {/* Member stats - OWNER only */}
                    {canViewAllUsage(myRole, userRole) && usage.memberStats.length > 0 && (
                      <div>
                        <p className="text-sm font-medium text-gray-700 mb-2">멤버별 사용량</p>
                        <div className="space-y-1">
                          {usage.memberStats.map((stat) => (
                            <div
                              key={stat.userId}
                              className="flex items-center justify-between text-sm"
                            >
                              <div className="flex items-center gap-2">
                                {roleIcon(stat.role)}
                                <span className="text-gray-700">{stat.name}</span>
                                <Badge className="bg-gray-100 text-gray-500 text-xs">
                                  {roleLabel(stat.role)}
                                </Badge>
                              </div>
                              <span className="font-medium">{stat.usedCount.toLocaleString()}건</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 text-center py-4">사용량 정보를 불러올 수 없습니다.</p>
                )}
              </CardContent>
            </Card>
          </div>
        </>
      )}

      {/* Create Team Dialog */}
      {showCreateDialog && (
        <CreateTeamDialog
          value={newTeamName}
          onChange={setNewTeamName}
          onSubmit={handleCreateTeam}
          onClose={() => { setShowCreateDialog(false); setNewTeamName(''); }}
          loading={actionLoading}
        />
      )}

      {/* 결제수단 선택 Dialog (팀 생성 / 엔터프라이즈 계약 신규 생성 공용) */}
      <PaymentMethodSelectDialog
        open={paymentMethodDialog.open}
        title={paymentMethodDialog.title}
        priceLabel={paymentMethodDialog.priceLabel}
        cards={cards}
        submitting={paymentMethodSubmitting}
        onSelect={handlePaymentMethodSelect}
        onClose={closePaymentMethodDialog}
      />

      {/* Transfer Owner Dialog */}
      {showTransferDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle>팀장 권한 이전</CardTitle>
              <CardDescription>
                팀장 권한을 이전하면 본인은 팀원(MEMBER)으로 변경됩니다. 팀 요금제는 변경되지 않습니다.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleTransferOwner} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">새 팀장 아이디</label>
                  <select
                    value={transferTargetUserId}
                    onChange={(e) => setTransferTargetUserId(e.target.value)}
                    className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm bg-white"
                    required
                  >
                    <option value="">-- 선택 --</option>
                    {members
                      .filter((m) => m.role !== 'OWNER')
                      .map((m) => (
                        <option key={m.userId} value={m.userId}>
                          {m.name} ({m.userId}) — {roleLabel(m.role)}
                        </option>
                      ))}
                  </select>
                </div>
                <div className="flex gap-2">
                  <Button type="submit" disabled={actionLoading || !transferTargetUserId} className="flex-1">
                    {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : '이전'}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => { setShowTransferDialog(false); setTransferTargetUserId(''); }}
                    disabled={actionLoading}
                    className="flex-1"
                  >
                    취소
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      {/* 팀 요금제 변경은 /dashboard/my/plan에서만 가능 */}

      {/* Delete Confirm Dialog */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle className="text-red-600">팀 삭제</CardTitle>
              <CardDescription className="space-y-2">
                <p>
                  <span className="font-semibold">{selectedTeam?.teamName}</span> 팀을 삭제하시겠습니까?
                  이 작업은 되돌릴 수 없습니다.
                </p>
                <ul className="text-xs list-disc list-inside space-y-0.5">
                  <li>팀 삭제 시 팀원들의 팀 멤버십이 비활성화됩니다.</li>
                  <li>개인 사용량은 독립적으로 유지됩니다.</li>
                  <li>결제 이력은 보존됩니다.</li>
                </ul>
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  className="flex-1 text-red-600 border-red-200 hover:bg-red-50"
                  onClick={handleDeleteTeam}
                  disabled={actionLoading}
                >
                  {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : '삭제'}
                </Button>
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setShowDeleteConfirm(false)}
                  disabled={actionLoading}
                >
                  취소
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* 엔터프라이즈 모달 */}
      <Dialog open={enterpriseModalOpen} onOpenChange={(o) => !enterpriseSaving && setEnterpriseModalOpen(o)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>팀 엔터프라이즈 요금제 설정</DialogTitle>
            <DialogDescription>월 사용량을 입력하면 자동으로 가격이 계산됩니다.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {/* 가격표 안내 */}
            <Alert>
              <AlertDescription className="text-xs space-y-1">
                <div className="font-semibold">💡 엔터프라이즈 요금제 가격표</div>
                {enterprisePricingTiers.length > 0 ? (
                  enterprisePricingTiers.map((tier, index) => (
                    <div key={index}>{tier.tier}: ₩{tier.unitPrice}/건</div>
                  ))
                ) : (
                  <div>가격표 로드 중...</div>
                )}
                <div className="text-gray-500 mt-1">* 사용량이 많아질수록 건당 단가 할인</div>
              </AlertDescription>
            </Alert>

            {/* 현재 계약 정보 */}
            {enterpriseContract && (
              <div className="bg-gray-50 rounded-lg p-3 space-y-2 text-sm">
                <div className="font-semibold">현재 계약</div>
                <div className="flex justify-between">
                  <span className="text-gray-500">계약명</span>
                  <span>{enterpriseContract.contractName}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">월 사용량</span>
                  <span>{enterpriseContract.monthlyLimit.toLocaleString()}건</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">월 이용료</span>
                  <span>₩{enterpriseContract.monthlyPrice.toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">계약 기간</span>
                  <span>{new Date(enterpriseContract.startDate).toLocaleDateString('ko-KR')} ~ {new Date(enterpriseContract.endDate).toLocaleDateString('ko-KR')}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">자동 갱신</span>
                  <span>{enterpriseContract.autoRenewal ? '활성' : '비활성'}</span>
                </div>
              </div>
            )}

            {/* 사용량 입력 */}
            <div>
              <Label htmlFor="team-enterprise-usage">팀 월 사용량 (5,001건 ~ 1,000,000건)</Label>
              <Input
                id="team-enterprise-usage"
                type="number"
                min={5001}
                max={1000000}
                value={enterpriseUsage}
                onChange={(e) => handleEnterpriseUsageChange(e.target.value)}
                disabled={enterpriseSaving}
                className="mt-1"
              />
            </div>

            {/* 자동 계산된 가격 표시 */}
            <div className="bg-blue-50 p-4 rounded-lg">
              <div className="text-sm text-gray-600 mb-1">팀 월 이용료</div>
              <div className="text-2xl font-bold text-blue-700">₩{enterprisePrice.toLocaleString()}</div>
              {enterprisePriceInfo && (
                <div className="text-xs text-gray-500 mt-1">
                  건당 ₩{enterprisePriceInfo.unitPrice} ({enterprisePriceInfo.tier})
                </div>
              )}
            </div>

            {/* 업그레이드/다운그레이드 표시 */}
            {enterpriseContract && (
              <div className="bg-gray-50 p-4 rounded-lg">
                {enterpriseUsage > enterpriseContract.monthlyLimit ? (
                  <div className="flex items-center gap-2 text-green-700">
                    <TrendingUp className="w-5 h-5" />
                    <div>
                      <div className="font-semibold">업그레이드</div>
                      <div className="text-xs text-gray-600">비례 계산 금액이 청구됩니다</div>
                    </div>
                  </div>
                ) : enterpriseUsage < enterpriseContract.monthlyLimit ? (
                  <div className="flex items-center gap-2 text-orange-700">
                    <TrendingDown className="w-5 h-5" />
                    <div>
                      <div className="font-semibold">다운그레이드</div>
                      <div className="text-xs text-gray-600">청구 기간 다음 날부터 적용됩니다</div>
                      <div className="text-xs text-gray-600">즉시 결제는 발생하지 않습니다</div>
                    </div>
                  </div>
                ) : null}
              </div>
            )}

            {/* 에러 메시지 */}
            {enterpriseError && (
              <Alert variant="destructive">
                <AlertCircle className="w-4 h-4" />
                <AlertDescription className="text-xs">{enterpriseError}</AlertDescription>
              </Alert>
            )}

            {/* 자동 갱신 설정 */}
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="autoRenewal"
                checked={enterpriseAutoRenewal}
                onChange={(e) => setEnterpriseAutoRenewal(e.target.checked)}
                className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
              />
              <label htmlFor="autoRenewal" className="text-sm text-gray-700 cursor-pointer">
                계약 만료 시 자동 갱신
              </label>
            </div>

            {/* 저장 버튼 */}
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setEnterpriseModalOpen(false)}
                disabled={enterpriseSaving}
              >
                취소
              </Button>
              <Button onClick={handleEnterpriseSave} disabled={enterpriseSaving}>
                {enterpriseSaving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                저장
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* 다운그레이드 결과 Dialog */}
      <Dialog open={downgradeResult.open} onOpenChange={(open) => setDowngradeResult({ ...downgradeResult, open })}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>다운그레이드 예약 완료</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <DialogDescription>
              {downgradeResult.message}
            </DialogDescription>
            {downgradeResult.effectiveDate && (
              <div className="bg-gray-50 rounded-lg p-4 space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">적용일</span>
                  <span className="font-semibold">{downgradeResult.effectiveDate}</span>
                </div>
                {downgradeResult.nextBillingAmount && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">다음 청구 금액</span>
                    <span className="font-semibold">₩{downgradeResult.nextBillingAmount.toLocaleString()}</span>
                  </div>
                )}
              </div>
            )}
            <Button onClick={() => setDowngradeResult({ open: false, message: '' })}>
              확인
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ── Helper sub-component ──
interface CreateTeamDialogProps {
  value: string;
  onChange: (v: string) => void;
  onSubmit: (e: React.FormEvent) => void;
  onClose: () => void;
  loading: boolean;
}
const CreateTeamDialog: React.FC<CreateTeamDialogProps> = ({
  value,
  onChange,
  onSubmit,
  onClose,
  loading,
}) => (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
    <Card className="w-full max-w-md">
      <CardHeader>
        <CardTitle>새 팀 만들기</CardTitle>
        <CardDescription>팀 이름을 입력해주세요.</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">
              팀 이름 <span className="text-red-500">*</span>
            </label>
            <Input
              value={value}
              onChange={(e) => onChange(e.target.value)}
              placeholder="예: 개발팀"
              required
              disabled={loading}
              autoFocus
              maxLength={100}
            />
          </div>
          <div className="bg-blue-50 p-3 rounded-lg text-sm text-gray-600">
            <p>팀 결제 주기는 팀장의 결제 주기와 동일하게 설정됩니다.</p>
          </div>
          <div className="flex gap-2">
            <Button type="submit" disabled={loading || !value.trim()} className="flex-1">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : '생성'}
            </Button>
            <Button type="button" variant="outline" onClick={onClose} disabled={loading} className="flex-1">
              취소
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  </div>
);

/**
 * 결제수단 선택 다이얼로그 - 팀 생성 / 팀 엔터프라이즈 계약 신규 생성에서 공용으로 사용.
 * 일반 플랜 변경(PlanPage.tsx)의 결제 방법 선택 모달과 동일한 UI 패턴을 따르되,
 * 프로레이션이 없는 단순 케이스(신규 생성은 항상 전액 청구)만 다룬다.
 */
interface PaymentMethodSelectDialogProps {
  open: boolean;
  title: string;
  priceLabel: string;
  cards: CardInfo[];
  submitting: boolean;
  onSelect: (method: 'CARD' | 'BANK_TRANSFER') => void;
  onClose: () => void;
}
const PaymentMethodSelectDialog: React.FC<PaymentMethodSelectDialogProps> = ({
  open,
  title,
  priceLabel,
  cards,
  submitting,
  onSelect,
  onClose,
}) => {
  if (!open) return null;
  const defaultCard = cards.find(c => c.isDefault) ?? cards[0];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>{title}</CardTitle>
          <CardDescription>{priceLabel}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <button
              type="button"
              onClick={() => onSelect('BANK_TRANSFER')}
              disabled={submitting}
              className="w-full flex items-center gap-3 p-3 rounded-lg border-2 border-gray-200 hover:border-gray-300 hover:bg-gray-50 transition-all"
            >
              <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
                <Building2 className="w-5 h-5 text-gray-600" />
              </div>
              <div className="flex-1 text-left">
                <div className="font-semibold text-gray-800 text-sm">계좌이체</div>
                <div className="text-xs text-gray-500">입금 후 관리자 확인 - 확인 전까지는 이용이 제한됩니다</div>
              </div>
            </button>

            <button
              type="button"
              onClick={() => isCardPaymentEnabled && onSelect('CARD')}
              disabled={submitting || !isCardPaymentEnabled}
              className={`w-full flex items-center gap-3 p-3 rounded-lg border-2 transition-all ${
                !isCardPaymentEnabled
                  ? 'border-gray-100 opacity-50 cursor-not-allowed'
                  : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
              }`}
            >
              <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
                <CreditCard className="w-5 h-5 text-gray-600" />
              </div>
              <div className="flex-1 text-left">
                <div className="font-semibold text-gray-800 text-sm">카드 결제</div>
                <div className="text-xs text-gray-500">
                  {!isCardPaymentEnabled
                    ? '곧 서비스 예정'
                    : defaultCard ? `등록된 카드(${defaultCard.cardCompany} ${defaultCard.cardNumberMasked})로 즉시 결제` : '카드 등록 필요'}
                </div>
              </div>
            </button>

            <Alert className="border-blue-200 bg-blue-50 py-2">
              <AlertCircle className="w-4 h-4 text-blue-600" />
              <AlertDescription className="text-blue-700 text-xs">
                {isCardPaymentEnabled
                  ? '계좌이체는 관리자 입금 확인 전까지 사용이 제한됩니다. 카드 결제는 등록된 카드가 없으면 먼저 마이페이지에서 카드를 등록해야 합니다.'
                  : '계좌이체는 관리자 입금 확인 전까지 사용이 제한됩니다. 카드 결제는 곧 서비스될 예정입니다.'}
              </AlertDescription>
            </Alert>

            <div className="flex justify-end">
              <Button variant="outline" onClick={onClose} disabled={submitting}>
                취소
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
